import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';
import { AuthLayout } from '../components/AuthLayout';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('A senha deve ter ao menos 8 caracteres.');
    if (password !== confirm) return setError('As senhas não conferem.');
    setSubmitting(true);
    try {
      await api.confirmPasswordReset({ token, newPassword: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      if (err instanceof PnlApiError) {
        if (err.api.code === 'INVALID_TOKEN' || err.api.code === 'EXPIRED_TOKEN')
          setError('Link inválido ou expirado. Solicite um novo.');
        else if (err.api.code === 'ALREADY_USED') setError('Este link já foi utilizado.');
        else setError(err.api.message);
      } else {
        setError('Erro de conexão. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <h1 className="font-display text-ink-100 text-xl font-semibold">Definir nova senha</h1>

      {!token ? (
        <div className="bg-danger-500/10 border-danger-500/30 text-danger-600 mt-5 rounded-lg border px-3 py-2 text-sm">
          Link inválido. Solicite a redefinição novamente em{' '}
          <Link to="/forgot-password" className="underline">
            Esqueci minha senha
          </Link>
          .
        </div>
      ) : done ? (
        <div className="mt-6">
          <div className="bg-success-500/10 border-success-500/30 text-success-600 rounded-lg border px-3 py-3 text-sm">
            Senha alterada com sucesso! Redirecionando para o login…
          </div>
        </div>
      ) : (
        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-danger-500/10 border-danger-500/30 text-danger-600 rounded-lg border px-3 py-2 text-sm">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="pw" className="text-ink-300 mb-1.5 block text-xs font-semibold">
              Nova senha
            </label>
            <div className="relative">
              <Lock size={14} className="text-ink-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                id="pw"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="border-ink-800 text-ink-100 placeholder:text-ink-500 focus:border-navy-primary focus:ring-navy-primary/20 w-full rounded-lg border bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2"
                disabled={submitting}
              />
            </div>
          </div>
          <div>
            <label htmlFor="pw2" className="text-ink-300 mb-1.5 block text-xs font-semibold">
              Confirmar senha
            </label>
            <div className="relative">
              <Lock size={14} className="text-ink-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                id="pw2"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repita a senha"
                className="border-ink-800 text-ink-100 placeholder:text-ink-500 focus:border-navy-primary focus:ring-navy-primary/20 w-full rounded-lg border bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2"
                disabled={submitting}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="bg-navy-primary hover:bg-navy-primary/90 shadow-soft mt-2 w-full rounded-lg py-3 text-sm font-semibold text-white transition-colors disabled:opacity-50"
          >
            {submitting ? 'Salvando…' : 'Salvar nova senha'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
